/**
 * ChatConsent
 *
 * Purpose: Asks before loading the Zoho SalesIQ live chat (it sets cookies and shares the chat with Zoho),
 * then loads the widget only if the visitor allows it.
 * Data schema: No props. The choice is kept in localStorage under dopove-chat-consent.
 * Accessibility: A non-modal region that never traps focus; both choices are ordinary buttons.
 * Copy source: content/website-copy-deck.md (consent.*).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SALESIQ_SRC } from "../config/salesiq";
import { readConsent, writeConsent, RESET_EVENT } from "../utils/chatConsent";

// Zoho's snippet: an init stub first, then the deferred widget script.
const loadSalesIQ = () => {
  if (document.getElementById("zsiqscript")) return;
  window.$zoho = window.$zoho || {};
  window.$zoho.salesiq = window.$zoho.salesiq || { ready: function () {} };
  const script = document.createElement("script");
  script.id = "zsiqscript";
  script.src = SALESIQ_SRC;
  script.defer = true;
  document.body.appendChild(script);
};

export default function ChatConsent() {
  const [decision, setDecision] = useState(readConsent);
  // Prerendering and test runners drive an automated browser; they should neither see a banner nor load the widget.
  const automated = typeof navigator !== "undefined" && navigator.webdriver === true;

  useEffect(() => {
    if (!automated && decision === "granted") loadSalesIQ();
  }, [automated, decision]);

  useEffect(() => {
    const reset = () => setDecision(null);
    window.addEventListener(RESET_EVENT, reset);
    return () => window.removeEventListener(RESET_EVENT, reset);
  }, []);

  if (automated || decision) return null;

  const choose = (value) => {
    writeConsent(value);
    setDecision(value);
  };

  return (
    <section
      aria-label="Chat preferences"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 rounded-2xl border border-[#FFFFFF]/10 bg-[#191d2b]/95 backdrop-blur p-5 shadow-2xl"
    >
      <p className="text-sm leading-relaxed text-[#FFFFFF]/80">
        Chat with the Dopove team. It sets cookies and keeps your messages so we can reply. Allow it?
      </p>
      <p className="mt-2 text-xs text-[#FFFFFF]/60">
        <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-[#FFFFFF] focus-visible-ring">
          Privacy Policy
        </Link>
      </p>
      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={() => choose("granted")}
          className="px-5 py-2 rounded-full bg-[#1E7FD1] text-white text-sm font-bold hover-scale-press focus-visible-ring"
        >
          Allow chat
        </button>
        <button
          type="button"
          onClick={() => choose("denied")}
          className="px-5 py-2 rounded-full border border-[#FFFFFF]/20 text-[#FFFFFF] text-sm font-bold hover-scale-press focus-visible-ring"
        >
          No thanks
        </button>
      </div>
    </section>
  );
}
